import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate, Outlet } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Toaster } from "sonner";
import { I18nProvider } from "@/i18n";
import { SessionProvider, useSession } from "@/context/SessionContext";
import { LaunchProvider } from "@/context/LaunchContext";
import { FollowProvider } from "@/features/follow/FollowContext";
import { AnalysisListProvider } from "@/features/analysis/AnalysisListContext";
import { AppShell } from "@/components/shell/AppShell";
import { PublicLayout } from "@/pages/public/PublicLayout";

import Landing from "@/pages/public/Landing";
import { ProductPage, AtlasPublic, PerformancePublic, PricingPage, FaqPage } from "@/pages/public/MarketingPages";
import { Contact, Terms, Privacy, Refunds, Cancellation, ResponsibleUse } from "@/pages/public/LegalPages";
import { LivePublic, PrematchPublic } from "@/pages/public/PublicIntel";
import { Login, Register } from "@/pages/public/Auth";

import Dashboard from "@/pages/app/Dashboard";
import Live from "@/pages/app/Live";
import Prematch from "@/pages/app/Prematch";
import MatchRoute from "@/pages/app/MatchRoute";
import Atlas from "@/pages/app/Atlas";
import Performance from "@/pages/app/Performance";
import History from "@/pages/app/History";
import Notifications from "@/pages/app/Notifications";
import Membership from "@/pages/app/Membership";
import CouponCenter from "@/pages/app/CouponCenter";
import Support from "@/pages/app/Support";
import Admin from "@/pages/app/Admin";
import Account from "@/pages/app/Account";
import Settings from "@/pages/app/Settings";
import AnalysisList from "@/pages/app/AnalysisList";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

// Neutral loading state while an existing token is validated against /auth/me.
function GateLoader() {
  return (
    <div className="grid min-h-screen place-items-center bg-navy-950" data-testid="auth-gate-loading">
      <Loader2 className="h-7 w-7 animate-spin text-atlas-400" />
    </div>
  );
}

// Real auth gate for /app/*. No guest/preview fallback. While validating, show a
// neutral loader (never flash protected content). Unauthenticated → /login,
// preserving the originally requested location so we can return after sign-in.
function RequireAuthLayout() {
  const { isAuthenticated, loading } = useSession();
  const location = useLocation();
  if (loading) return <GateLoader />;
  if (!isAuthenticated) return <Navigate to="/login" state={{ from: location }} replace />;
  return <Outlet />;
}

function App() {
  return (
    <I18nProvider>
      <SessionProvider>
        <LaunchProvider>
          <FollowProvider>
          <AnalysisListProvider>
            <BrowserRouter>
              <ScrollToTop />
              <Toaster theme="dark" position="top-right" richColors />
              <Routes>
                {/* public marketing */}
                <Route element={<PublicLayout />}>
                  <Route path="/" element={<Landing />} />
                  <Route path="/product" element={<ProductPage />} />
                  <Route path="/live" element={<LivePublic />} />
                  <Route path="/prematch" element={<PrematchPublic />} />
                  <Route path="/atlas" element={<AtlasPublic />} />
                  <Route path="/performance" element={<PerformancePublic />} />
                  <Route path="/pricing" element={<PricingPage />} />
                  <Route path="/faq" element={<FaqPage />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/terms" element={<Terms />} />
                  <Route path="/privacy" element={<Privacy />} />
                  <Route path="/refunds" element={<Refunds />} />
                  <Route path="/cancellation" element={<Cancellation />} />
                  <Route path="/responsible-use" element={<ResponsibleUse />} />
                </Route>

                {/* auth */}
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />

                {/* authenticated app — gated by real /auth/me session */}
                <Route element={<RequireAuthLayout />}>
                  <Route path="/app" element={<AppShell><Dashboard /></AppShell>} />
                  <Route path="/app/live" element={<AppShell><Live /></AppShell>} />
                  <Route path="/app/prematch" element={<AppShell><Prematch /></AppShell>} />
                  <Route path="/app/match/:id" element={<AppShell><MatchRoute /></AppShell>} />
                  <Route path="/app/atlas" element={<AppShell><Atlas /></AppShell>} />
                  <Route path="/app/performance" element={<AppShell><Performance /></AppShell>} />
                  <Route path="/app/history" element={<AppShell><History /></AppShell>} />
                  <Route path="/app/analysis" element={<AppShell><AnalysisList /></AppShell>} />
                  <Route path="/app/notifications" element={<AppShell><Notifications /></AppShell>} />
                  <Route path="/app/membership" element={<AppShell><Membership /></AppShell>} />
                  <Route path="/app/coupons" element={<AppShell><CouponCenter /></AppShell>} />
                  <Route path="/app/support" element={<AppShell><Support /></AppShell>} />
                  <Route path="/app/admin" element={<AppShell><Admin /></AppShell>} />
                  <Route path="/app/account" element={<AppShell><Account /></AppShell>} />
                  <Route path="/app/settings" element={<AppShell><Settings /></AppShell>} />
                </Route>

                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </BrowserRouter>
          </AnalysisListProvider>
        </FollowProvider>
        </LaunchProvider>
      </SessionProvider>
    </I18nProvider>
  );
}

export default App;
